import WaterMaterial from "../rendering/water-material";
import getPersistentUrl from "../utils/persistent-path";
import RenderManager from "../rendering/render-manager";

class Water extends THREE.Mesh {
    constructor(options) {
        options = Object.assign({
            alpha: 1.0,
            sunDirection: new THREE.Vector3(0, 1, 0),
            sunColor: 0xffffff,
            waterColor: 0x001e0f,
            distortionScale: 3.7,
            fog: false
        }, options || {});

        const planeWidth = options.planeWidth !== undefined ? options.planeWidth : 4;
        const planeHeight = options.planeHeight !== undefined ? options.planeHeight : 4;

        const textureWidth = options.textureWidth !== undefined ? options.textureWidth : 512;
        const textureHeight = options.textureHeight !== undefined ? options.textureHeight : 512;

        const alpha = options.alpha !== undefined ? options.alpha : 1.0;
        const time = options.time !== undefined ? options.time : 0.0;
        const normalSampler = options.waterNormals !== undefined ? options.waterNormals : new THREE.TextureLoader().load(getPersistentUrl("water/waternormals.jpg"), function (texture) { texture.wrapS = texture.wrapT = THREE.RepeatWrapping; });
        const sunDirection = options.sunDirection !== undefined ? options.sunDirection : new THREE.Vector3(0.70707, 0.70707, 0.0);
        const sunColor = new THREE.Color(options.sunColor !== undefined ? options.sunColor : 0xffffff);
        const waterColor = new THREE.Color(options.waterColor !== undefined ? options.waterColor : 0x7F7F7F);
        const eye = options.eye !== undefined ? options.eye : new THREE.Vector3(0, 0, 0);
        const distortionScale = options.distortionScale !== undefined ? options.distortionScale : 20.0;
        const side = options.side !== undefined ? options.side : THREE.FrontSide;
        const fog = options.fog !== undefined ? options.fog : false;
        const size = options.size !== undefined ? options.size : 10.0;

        const parameters = {
            minFilter: THREE.LinearFilter,
            magFilter: THREE.LinearFilter,
            format: THREE.RGBFormat,
            stencilBuffer: false
        };

        const renderTarget = new THREE.WebGLRenderTarget(textureWidth, textureHeight, parameters);

        if (!THREE.Math.isPowerOfTwo(textureWidth) || !THREE.Math.isPowerOfTwo(textureHeight)) {
            renderTarget.texture.generateMipmaps = false;
        }
        const textureMatrix = new THREE.Matrix4();
        const material = new WaterMaterial({
            normalSampler, mirrorSampler: renderTarget.texture,
            alpha, time,
            distortionScale, textureMatrix,
            sunColor, sunDirection,
            eye, waterColor,
            side, fog, size
        });

        const geometry = new THREE.PlaneBufferGeometry(planeWidth, planeHeight, 2, 2);
        // const geometry = new THREE.SphereBufferGeometry(1, 128, 128);

        geometry.rotateX(-Math.PI / 2);

        super(geometry, material);

        this.type = "Water";
        this.name = "Water";
        this.isWater = true;

        this.eye = eye;
        this.clipBias = options.clipBias !== undefined ? options.clipBias : 0.0;
        this.textureMatrix = textureMatrix;
        this.mirrorPlane = new THREE.Plane();
        this.normal = new THREE.Vector3();
        this.mirrorWorldPosition = new THREE.Vector3();
        this.cameraWorldPosition = new THREE.Vector3();
        this.rotationMatrix = new THREE.Matrix4();
        this.lookAtPosition = new THREE.Vector3(0, 0, - 1);
        this.clipPlane = new THREE.Vector4();

        this.view = new THREE.Vector3();
        this.target = new THREE.Vector3();
        this.q = new THREE.Vector4();

        this.renderTarget = renderTarget;
        this.mirrorCamera = new THREE.PerspectiveCamera();
        this.clock = new THREE.Clock();
    }

    renderWater(renderer, scene, camera) {
        const renderTarget = this.renderTarget;
        const eye = this.eye;
        const clipBias = this.clipBias;
        const textureMatrix = this.textureMatrix;
        const mirrorPlane = this.mirrorPlane;
        const normal = this.normal;
        const mirrorWorldPosition = this.mirrorWorldPosition;
        const cameraWorldPosition = this.cameraWorldPosition;
        const rotationMatrix = this.rotationMatrix;
        const lookAtPosition = this.lookAtPosition;
        const clipPlane = this.clipPlane;
        const view = this.view;
        const target = this.target;
        const q = this.q;
        const mirrorCamera = this.mirrorCamera;

        mirrorWorldPosition.setFromMatrixPosition(this.matrixWorld);
        cameraWorldPosition.setFromMatrixPosition(camera.matrixWorld);

        const matrix = new THREE.Matrix4().makeRotationAxis(new THREE.Vector3(1, 0, 0), -Math.PI * 0.5);

        rotationMatrix.extractRotation(this.matrixWorld).multiply(matrix);

        normal.set(0, 0, 1);
        normal.applyMatrix4(rotationMatrix);

        view.subVectors(mirrorWorldPosition, cameraWorldPosition);

        // Avoid rendering when mirror is facing away

        if (view.dot(normal) > 0) return;

        view.reflect(normal).negate();
        view.add(mirrorWorldPosition);

        rotationMatrix.extractRotation(camera.matrixWorld);

        lookAtPosition.set(0, 0, - 1);
        lookAtPosition.applyMatrix4(rotationMatrix);
        lookAtPosition.add(cameraWorldPosition);

        target.subVectors(mirrorWorldPosition, lookAtPosition);
        target.reflect(normal).negate();
        target.add(mirrorWorldPosition);

        mirrorCamera.position.copy(view);
        mirrorCamera.up.set(0, 1, 0);
        mirrorCamera.up.applyMatrix4(rotationMatrix);
        mirrorCamera.up.reflect(normal);
        mirrorCamera.lookAt(target);

        mirrorCamera.far = camera.far; // Used in WebGLBackground

        mirrorCamera.updateMatrixWorld();
        mirrorCamera.projectionMatrix.copy(camera.projectionMatrix);

        // Update the texture matrix
        textureMatrix.set(
            0.5, 0.0, 0.0, 0.5,
            0.0, 0.5, 0.0, 0.5,
            0.0, 0.0, 0.5, 0.5,
            0.0, 0.0, 0.0, 1.0
        );

        textureMatrix.multiply(mirrorCamera.projectionMatrix);
        textureMatrix.multiply(mirrorCamera.matrixWorldInverse);

        // Now update projection matrix with new clip plane, implementing code from: http://www.terathon.com/code/oblique.html
        // Paper explaining this technique: http://www.terathon.com/lengyel/Lengyel-Oblique.pdf
        mirrorPlane.setFromNormalAndCoplanarPoint(normal, mirrorWorldPosition);
        mirrorPlane.applyMatrix4(mirrorCamera.matrixWorldInverse);

        clipPlane.set(mirrorPlane.normal.x, mirrorPlane.normal.y, mirrorPlane.normal.z, mirrorPlane.constant);

        const projectionMatrix = mirrorCamera.projectionMatrix;

        q.x = (Math.sign(clipPlane.x) + projectionMatrix.elements[8]) / projectionMatrix.elements[0];
        q.y = (Math.sign(clipPlane.y) + projectionMatrix.elements[9]) / projectionMatrix.elements[5];
        q.z = - 1.0;
        q.w = (1.0 + projectionMatrix.elements[10]) / projectionMatrix.elements[14];

        // Calculate the scaled plane vector
        clipPlane.multiplyScalar(2.0 / clipPlane.dot(q));

        // Replacing the third row of the projection matrix
        projectionMatrix.elements[2] = clipPlane.x;
        projectionMatrix.elements[6] = clipPlane.y;
        projectionMatrix.elements[10] = clipPlane.z + 1.0 - clipBias;
        projectionMatrix.elements[14] = clipPlane.w;

        eye.setFromMatrixPosition(camera.matrixWorld);

        //

        const currentRenderTarget = renderer.getRenderTarget();

        const currentVrEnabled = renderer.vr.enabled;
        const currentShadowAutoUpdate = renderer.shadowMap.autoUpdate;

        this.visible = false;

        renderer.vr.enabled = false; // Avoid camera modification and recursion
        renderer.shadowMap.autoUpdate = false; // Avoid re-computing shadows

        renderer.setRenderTarget(renderTarget);
        renderer.clear();
        scene.background = new THREE.Color(0x74ccf4);

        renderer.render(scene, mirrorCamera, renderTarget);
        scene.background = null;
        this.visible = true;

        renderer.vr.enabled = currentVrEnabled;
        renderer.shadowMap.autoUpdate = currentShadowAutoUpdate;

        renderer.setRenderTarget(currentRenderTarget);
        this.material.uniforms.time.value = this.clock.getElapsedTime();

        RenderManager.containsAnimatedObjects = true;
    }
};

export default Water;
export { Water };
